import * as XLSX from "xlsx";
import type { Player, Position } from "../../shared/models/Player";
import { exportRankingsCsv, importRankingsCsv } from "../../shared/utils/xlsxRankings";

type Cell = string;

const HEADER_HINTS = {
  rank: new Set(["rank", "#", "overall", "overall rank", "overall_rank", "ovr", "rnk", "ecr"]),
  name: new Set(["name", "player", "player name", "player_name", "full name", "full_name"]),
  position: new Set(["position", "pos", "player position", "player_position"]),
  team: new Set(["team", "tm", "nfl team", "nfl_team"]),
  tier: new Set(["tier", "tiers", "pos tier", "position tier", "position_tier", "pos_tier"]),
};

const POSITION_VALUES = new Set(["QB", "RB", "WR", "TE", "K", "PK", "DST", "D/ST", "D-ST", "DEF", "DEFENSE"]);

function cleanCell(value: unknown): Cell {
  return String(value ?? "").trim();
}

function normalizeHeader(value: string) {
  return value.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

function normalizeTeam(value: string) {
  return value.toUpperCase().replace(/[^A-Z]/g, "").trim();
}

function normalizePosition(value: string): Position | "" {
  const raw = value.trim().toUpperCase();
  const normalized = raw.replace(/\d+$/, "");
  if (normalized === "QB" || normalized === "RB" || normalized === "WR" || normalized === "TE" || normalized === "K") return normalized;
  if (normalized === "PK") return "K";
  if (normalized === "DST" || normalized === "D/ST" || normalized === "D-ST" || normalized === "DEF" || normalized === "DEFENSE") return "DST";
  return "";
}

function isRankLike(value: string) {
  return /^\d{1,4}$/.test(value.replace(/,/g, ""));
}

function parseTierNumber(value: string): number | undefined {
  const match = value.trim().match(/\d+/);
  if (!match) return undefined;
  const parsed = Number.parseInt(match[0], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function isLikelyName(value: string) {
  if (!value) return false;
  if (isRankLike(value)) return false;
  if (POSITION_VALUES.has(value.toUpperCase())) return false;
  const team = normalizeTeam(value);
  if (team.length >= 2 && team.length <= 4 && team === value.toUpperCase()) return false;
  return /[A-Za-z]/.test(value) && value.split(/\s+/).length >= 2;
}

function detectHeaderRow(rows: Cell[][]) {
  const firstRow = rows[0] ?? [];
  const matches = firstRow.reduce((count, cell) => {
    const header = normalizeHeader(cell);
    return count +
      (HEADER_HINTS.rank.has(header) ||
      HEADER_HINTS.name.has(header) ||
      HEADER_HINTS.position.has(header) ||
      HEADER_HINTS.team.has(header)
        ? 1
        : 0);
  }, 0);
  return matches >= 2;
}

function scoreColumn(rows: Cell[][], columnIndex: number, matcher: (value: string) => boolean) {
  let matches = 0;
  let total = 0;
  for (const row of rows) {
    const value = cleanCell(row[columnIndex]);
    if (!value) continue;
    total += 1;
    if (matcher(value)) matches += 1;
  }
  if (!total) return 0;
  return matches / total;
}

function findHeaderIndex(headers: Cell[], hints: Set<string>) {
  return headers.findIndex((header) => hints.has(normalizeHeader(header)));
}

function inferColumnIndexes(rows: Cell[][]) {
  const hasHeader = detectHeaderRow(rows);
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const headers = hasHeader ? rows[0] ?? [] : [];
  const width = Math.max(...rows.map((row) => row.length), 0);

  const explicitName = hasHeader ? findHeaderIndex(headers, HEADER_HINTS.name) : -1;
  const explicitRank = hasHeader ? findHeaderIndex(headers, HEADER_HINTS.rank) : -1;
  const explicitPosition = hasHeader ? findHeaderIndex(headers, HEADER_HINTS.position) : -1;
  const explicitTeam = hasHeader ? findHeaderIndex(headers, HEADER_HINTS.team) : -1;
  const explicitTier = hasHeader ? findHeaderIndex(headers, HEADER_HINTS.tier) : -1;

  let nameIndex = explicitName;
  let rankIndex = explicitRank;
  let positionIndex = explicitPosition;
  let teamIndex = explicitTeam;

  if (nameIndex < 0) {
    let bestScore = -1;
    for (let i = 0; i < width; i += 1) {
      const score = scoreColumn(dataRows, i, isLikelyName);
      if (score > bestScore) {
        bestScore = score;
        nameIndex = i;
      }
    }
  }

  if (positionIndex < 0) {
    let bestScore = -1;
    for (let i = 0; i < width; i += 1) {
      const score = scoreColumn(dataRows, i, (value) => Boolean(normalizePosition(value)));
      if (score > bestScore) {
        bestScore = score;
        positionIndex = i;
      }
    }
  }

  if (teamIndex < 0) {
    let bestScore = -1;
    for (let i = 0; i < width; i += 1) {
      const score = scoreColumn(dataRows, i, (value) => {
        const team = normalizeTeam(value);
        return team.length >= 2 && team.length <= 4;
      });
      if (score > bestScore) {
        bestScore = score;
        teamIndex = i;
      }
    }
  }

  if (rankIndex < 0) {
    let bestScore = -1;
    for (let i = 0; i < width; i += 1) {
      const score = scoreColumn(dataRows, i, isRankLike);
      if (score > bestScore) {
        bestScore = score;
        rankIndex = i;
      }
    }
  }

  return { hasHeader, dataRows, nameIndex, rankIndex, positionIndex, teamIndex, tierIndex: explicitTier };
}

function rowsToCanonicalCsv(rows: Cell[][]) {
  const { dataRows, nameIndex, rankIndex, positionIndex, teamIndex, tierIndex } = inferColumnIndexes(rows);

  if (nameIndex < 0 || positionIndex < 0) {
    throw new Error("Could not identify player name and position columns.");
  }

  const csvPlayers: Player[] = [];
  const rankingIds: string[] = [];
  const tiersByPos: Record<Position, string[]> = { QB: [], RB: [], WR: [], TE: [], K: [], DST: [] };
  const lastTierByPos: Partial<Record<Position, number>> = {};
  const seenSourceTiersByPos: Record<Position, Set<number>> = {
    QB: new Set<number>(),
    RB: new Set<number>(),
    WR: new Set<number>(),
    TE: new Set<number>(),
    K: new Set<number>(),
    DST: new Set<number>(),
  };

  dataRows.forEach((row, index) => {
    const name = cleanCell(row[nameIndex]);
    const position = normalizePosition(cleanCell(row[positionIndex]));
    if (!name || !position) return;

    const team = teamIndex >= 0 ? normalizeTeam(cleanCell(row[teamIndex])) : "";
    const id = `expert-import-${index + 1}`;

    csvPlayers.push({
      id,
      name,
      position,
      team: team || undefined,
    });

    rankingIds.push(id);

    const currentTier = tierIndex >= 0 ? parseTierNumber(cleanCell(row[tierIndex])) : undefined;
    const lastTier = lastTierByPos[position];
    if (currentTier != null) {
      if (lastTier != null && currentTier !== lastTier && !seenSourceTiersByPos[position].has(currentTier)) {
        tiersByPos[position].push(id);
      }
      lastTierByPos[position] = currentTier;
      seenSourceTiersByPos[position].add(currentTier);
    }
  });

  if (!csvPlayers.length) {
    throw new Error("No importable expert rankings were found.");
  }

  const playersById = Object.fromEntries(csvPlayers.map((player) => [player.id, player]));
  const csv = exportRankingsCsv({
    rankingIds,
    playersById,
    tiersByPos: { QB: [], RB: [], WR: [], TE: [], K: [], DST: [] },
  });
  if (rankIndex >= 0) {
    const lines = csv.split("\n");
    const csvWithRanks = [
      lines[0],
      ...lines.slice(1).map((line, index) => {
        const rank = cleanCell(dataRows[index]?.[rankIndex]);
        if (!rank || !isRankLike(rank)) return line;
        return line.replace(/^"?(?:\d+)"?/, rank);
      }),
    ].join("\n");
    return { csvText: csvWithRanks, tiersByPos };
  }
  return { csvText: csv, tiersByPos };
}

function readWorkbookRows(arrayBuffer: ArrayBuffer): Cell[][] {
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const preferredSheetName = workbook.Sheets["Rankings"] ? "Rankings" : workbook.SheetNames?.[0];
  if (!preferredSheetName) return [];
  const sheet = workbook.Sheets[preferredSheetName];
  return (XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false }) as unknown[][])
    .map((row) => row.map(cleanCell))
    .filter((row) => row.some((cell) => cell));
}

export function importExpertRankingsFile(args: {
  fileName: string;
  arrayBuffer: ArrayBuffer;
}) {
  const rows = readWorkbookRows(args.arrayBuffer);
  const { csvText, tiersByPos } = rowsToCanonicalCsv(rows);
  const parsed = importRankingsCsv({ csvText, sortBy: "rank" });
  return {
    rankingIds: parsed.rankingIds,
    players: parsed.players,
    tiersByPos,
  };
}
